// src/components/services/useTelegram.ts
import { useEffect, useState } from 'react';

export interface TelegramUser {
    id: number;
    first_name: string;
    last_name?: string;
    username?: string;
    photo_url?: string;
    language_code?: string;
}

export interface TelegramWebApp {
    ready: () => void;
    expand: () => void;
    close: () => void;
    setHeaderColor?: (color: string) => void;
    setBackgroundColor?: (color: string) => void;
    showPopup?: (params: any) => void;
    showAlert?: (message: string) => void;
    showConfirm?: (message: string) => Promise<boolean>;
    initDataUnsafe?: {
        user?: TelegramUser;
        start_param?: string;
        auth_date?: number;
        hash?: string;
    };
    initData?: string;
    colorScheme?: 'light' | 'dark';
    themeParams?: Record<string, string>;
    isExpanded?: boolean;
    viewportHeight?: number;
    viewportStableHeight?: number;
    MainButton?: {
        text: string;
        color: string;
        textColor: string;
        isVisible: boolean;
        isActive: boolean;
        show: () => void;
        hide: () => void;
        setText: (text: string) => void;
        onClick: (callback: () => void) => void;
    };
    BackButton?: {
        isVisible: boolean;
        show: () => void;
        hide: () => void;
        onClick: (callback: () => void) => void;
    };
    platform?: string;
}

declare global {
    interface Window {
        Telegram?: {
            WebApp: TelegramWebApp;
        };
    }
}

interface UseTelegramReturn {
    tg: TelegramWebApp | null;
    user: TelegramUser | null;
    isTelegram: boolean;
    isReady: boolean;
    initData: string | null;
    showAlert: (message: string) => void;
    showConfirm: (message: string) => Promise<boolean>;
    closeApp: () => void;
}

export function useTelegram(): UseTelegramReturn {
    const [isReady, setIsReady] = useState(false);
    const [tg, setTg] = useState<TelegramWebApp | null>(null);
    const [user, setUser] = useState<TelegramUser | null>(null);
    const [initData, setInitData] = useState<string | null>(null);
    const [isTelegram, setIsTelegram] = useState(false);

    useEffect(() => {
        // Проверяем, запущено ли приложение в Telegram
        const telegramWebApp = (window as any).Telegram?.WebApp;

        // Более надежная проверка: наличие initData и платформы
        const hasTelegramData = telegramWebApp &&
            telegramWebApp.initData &&
            telegramWebApp.initData.length > 0;

        if (hasTelegramData) {
            console.log('Запущено в Telegram Mini App');
            // Инициализируем Telegram WebApp
            telegramWebApp.ready();
            telegramWebApp.expand();

            setIsTelegram(true);
            setTg(telegramWebApp);

            // Получаем данные пользователя
            if (telegramWebApp.initDataUnsafe?.user) {
                setUser(telegramWebApp.initDataUnsafe.user);
            }

            // Получаем initData для проверки на сервере
            if (telegramWebApp.initData) {
                setInitData(telegramWebApp.initData);
            }
        } else {
            console.log('Запущено на сайте');
            // Приложение запущено не в Telegram
            setIsTelegram(false);
        }

        setIsReady(true);
    }, []);

    const showAlert = (message: string) => {
        if (tg?.showAlert) {
            tg.showAlert(message);
        } else {
            alert(message);
        }
    };

    const showConfirm = async (message: string): Promise<boolean> => {
        if (tg?.showConfirm) {
            return tg.showConfirm(message);
        } else {
            return window.confirm(message);
        }
    };

    const closeApp = () => {
        if (tg?.close) {
            tg.close();
        } else {
            window.close();
        }
    };

    return {
        tg,
        user,
        isTelegram,
        isReady,
        initData,
        showAlert,
        showConfirm,
        closeApp
    };
}